<script lang="ts" setup>
import ProductCard from '~/components/cards/ProductCard.vue'
import FilterActiveCatsSection from '~/components/filters/FilterActiveCatsSection.vue'
import FilterBodyCatsSection from '~/components/filters/FilterBodyCatsSection.vue'
import FilterBottomPopup from '~/components/filters/FilterBottomPopup.vue'
import FilterBrandsSection from '~/components/filters/FilterBrandsSection.vue'
import FilterButton from '~/components/filters/FilterButton.vue'
import FilterCategoriesSection from '~/components/filters/FilterCategoriesSection.vue'
import FilterMobileButton from '~/components/filters/FilterMobileButton.vue'
import FilterMobileItem from '~/components/filters/FilterMobileItem.vue'
import FilterMobilePopup from '~/components/filters/FilterMobilePopup.vue'
import FilterMobilePrice from '~/components/filters/FilterMobilePrice.vue'
import FilterRadioButtonsSection from '~/components/filters/FilterRadioButtonsSection.vue'
import FilterSidebar from '~/components/filters/FilterSidebar.vue'
import FilterState from '~/components/filters/FilterState.vue'
import FilterWrapper from '~/components/filters/FilterWrapper.vue'
import BasicSelect from '~/components/form-elements/BasicSelect.vue'
import SkeletonCard from '~/components/skeleton/SkeletonCard.vue'
import SkeletonSelect from '~/components/skeleton/SkeletonSelect.vue'
import SkeletonState from '~/components/skeleton/SkeletonState.vue'
import {useSearchProductsObserver} from '~/composables/search-products-observer'
import menuItems from '~/constants/menu-items'
import Page from '~/enums/page'
import { SearchboosterSort, SearchboosterSortUtils } from '~/enums/searchbooster-sort'
import YandexMetrikaGoal from '~/enums/yandex-metrika-goal'
import YandexMetrikaService from '~/services/yandex-metrika-service'
import {useSearchboosterStore} from '~/store/searchbooster'
import type {BreadcrumbsItem} from '~/types/breadcrumbs'
import type {SelectOption} from '~/types/form-elements'

const breadcrumbsItems: BreadcrumbsItem[] = [{ name: 'Главная', path: Page.Main }]
const title = 'Результаты поиска'

useHead({
  title: `${title} — iTAB`,
  meta: [{
    name: 'description',
    content: 'Description'
  }]
})

const sortOptions = (Object.keys(SearchboosterSort) as Array<keyof typeof SearchboosterSort>)
  .filter((key) => typeof SearchboosterSort[key] === 'string')
  .map((key) => {
    const value = SearchboosterSort[key]
    return {
      name: SearchboosterSortUtils.getName(value as SearchboosterSort),
      value: value as string
    }
  })

// const infiniteScrollTarget = ref<HTMLElement | null>(null)

const isPageLoading = ref(true)
const isStateLoading = ref(false)
const mobileCommonFiltersRef = ref()
const mobileFilterByActiveCatsRef = ref()
const mobileFilterByBodyCatsRef = ref()
const mobileFilterByBrandsRef = ref()
const mobileFilterByCategoriesRef = ref()
const mobilePriceRef = ref()
const mobileSortRef = ref()

const route = useRoute()
const router = useRouter()

const searchboosterStore = useSearchboosterStore()
const {
  isActiveFilter,
  isLoading,
  isSearchLoading,
  getCompletionsBrands,
  getCompletionsExtraOffers,
  getCountFilters,
  getHistory,
  getMaxPrice,
  getMinPrice,
  getPopularOffers,
  getPopularTips,
  getSearchOffers,
  getSearchProducts,
  getTips
} = storeToRefs(searchboosterStore)
useSearchProductsObserver()

const currentPage = computed(() => searchboosterStore.filter.skip / searchboosterStore.filter.limit + 1)

const lastPage = computed(() =>
  Math.ceil((searchboosterStore.searchMeta?.hits || 0) / searchboosterStore.filter.limit)
)

const sequenceNumber = computed(() => {
  return sortOptions.findIndex((option) => option.value === searchboosterStore.sort) || 0
})

// const { observe } = useInfiniteScroll(() => {
//   searchboosterStore.paginate()
// })

function handlerSort (x: SelectOption): void {
  const sort = SearchboosterSortUtils.getPropertyByValue(x.value)

  if (sort) {
    searchboosterStore.sort = sort
  }
}

function onBack (): void {
  mobileCommonFiltersRef.value.openPopup()
  mobileFilterByActiveCatsRef.value.closePopup()
  mobileFilterByBodyCatsRef.value.closePopup()
  mobileFilterByBrandsRef.value.closePopup()
  mobileFilterByCategoriesRef.value.closePopup()
}

async function changePage (pageNumber: number): Promise<void> {
  window.scrollTo(0, 0)

  searchboosterStore.filter.skip = (pageNumber - 1) * searchboosterStore.filter.limit

  await searchboosterStore.initSearch(false)
}

watch(
  () => ({
    isAvailable: searchboosterStore.filter.params.isAvailable,
    isRecommended: searchboosterStore.filter.params.isRecommended,
    limit: searchboosterStore.filter.limit,
    priceMax: searchboosterStore.filter.params.price.max,
    priceMin: searchboosterStore.filter.params.price.min,
    skip: searchboosterStore.filter.skip,
    query: searchboosterStore.filter.query,
  }),
  async (): Promise<void> => {
    await nextTick()

    // categoryId: newFilter.categoryId,
    const query = {
      ...router.currentRoute.value.query,
      q: searchboosterStore.filter.query,
      isAvailable: searchboosterStore.filter.params.isAvailable.toString(),
      isRecommended: searchboosterStore.filter.params.isRecommended.toString(),
      priceMin: searchboosterStore.filter.params.price.min ? searchboosterStore.filter.params.price.min.toString() : '',
      priceMax: searchboosterStore.filter.params.price.max ? searchboosterStore.filter.params.price.max.toString() : '',
      sort: searchboosterStore.sort,
    }

    await router.replace({ name: 'search', query })

    isStateLoading.value = true
    setTimeout(() => {
      isStateLoading.value = false
    }, 0)
  },
  { deep: true }
)

watch(
  () => [
    searchboosterStore.temp,
    searchboosterStore.filter.params.categories,
    searchboosterStore.filter.params.vendors,
  ],
  async (): Promise<void> => {
    await router.replace({
      name: 'search',
      query: {
        ...router.currentRoute.value.query,
        categories: searchboosterStore.filter.params.categories.join(','),
        categoriesMap: JSON.stringify(searchboosterStore.temp.categoriesMap),
        vendors: searchboosterStore.filter.params.vendors.join(','),
        vendorsMap: JSON.stringify(searchboosterStore.temp.vendorsMap),
      },
    })
  },
  { deep: true },
)

watch(
  () => [
    searchboosterStore.filter.params.isAvailable,
    // searchboosterStore.filter.params.isExpiring,
    searchboosterStore.filter.params.isRecommended,
    searchboosterStore.filter.params.price,
  ],
  async (): Promise<void> => {
    await searchboosterStore.initSearch(true)
  },
  { deep: true }
)

watch(() => searchboosterStore.sort, async (): Promise<void> => {
    await router.replace({
      name: 'search',
      query: {
        ...router.currentRoute.value.query,
        sort: searchboosterStore.sort,
      },
    })
    await searchboosterStore.initSearch(true)
  }
)

onMounted (async (): Promise<void> => {
  YandexMetrikaService.sendMetricEvent(YandexMetrikaGoal.ViewCatalog)

  searchboosterStore.updateFilterFromQuery(route.query)

  await searchboosterStore.initSearch(false)
  await searchboosterStore.fetchPopularProducts()

  isPageLoading.value = false
})
</script>

<template>
  <section itemscope itemtype="https://schema.org/SearchResultsPage">
    <div class="container">
      <Breadcrumbs class="!hidden sm:flex" :current="title" :items="breadcrumbsItems" />

      <FilterWrapper class="mb-5 md:hidden">
        <template #left>
          <FilterMobileButton
            v-if="mobileSortRef"
            class="mr-2"
            icon-left="outlined/sort-arrows"
            @click.prevent="mobileSortRef.openPopup"
          />

          <FilterMobileButton
            v-if="mobileCommonFiltersRef"
            class="mr-2"
            icon-left="outlined/filters"
            :indicator-value="getCountFilters"
            :is-indicator="isActiveFilter"
            name="Фильтры"
            @click.prevent="mobileCommonFiltersRef.openPopup"
          />

          <FilterMobileButton
            v-if="mobilePriceRef"
            class="mr-2"
            name="Цена"
            @click.prevent="mobilePriceRef.openPopup"
          />

          <FilterMobileButton
            v-if="mobileFilterByCategoriesRef"
            class="mr-2"
            icon-right="outlined/filter-arrow"
            name="Категория"
            @click.prevent="mobileFilterByCategoriesRef.openPopup"
          />

          <FilterMobileButton
            v-if="mobileFilterByBrandsRef"
            class="mr-2"
            icon-right="outlined/filter-arrow"
            name="Бренд"
            @click.prevent="mobileFilterByBrandsRef.openPopup"
          />

          <FilterMobileButton
            v-if="mobileFilterByActiveCatsRef"
            class="mr-2"
            icon-right="outlined/filter-arrow"
            name="Активное вещество"
            @click.prevent="mobileFilterByActiveCatsRef.openPopup"
          />

          <FilterMobileButton
            v-if="mobileFilterByBodyCatsRef"
            icon-right="outlined/filter-arrow"
            name="Система организма"
            @click.prevent="mobileFilterByBodyCatsRef.openPopup"
          />
        </template>
      </FilterWrapper>

      <CountResults
        class="mb-3 sm:mb-5"
        :count="searchboosterStore.searchMeta?.hits || 0"
        :is-loading="isSearchLoading"
        :query="searchboosterStore.filter.query"
      />

      <FilterMobileButton
        v-if="isActiveFilter"
        class="mb-3 !no-underline md:hidden"
        icon-left="outlined/delete"
        name="Очистить всё"
        @click.prevent="searchboosterStore.clearFilter"
      />

      <div class="flex-start -ml-1 flex flex-wrap items-center md:mb-10">
        <template v-if="getTips.length">
          <div v-for="(x, i) in getTips" :key="i" class="p-1">
            <FilterButton
              icon="outlined/new-search"
              :name="x.text.value"
              @click.prevent="searchboosterStore.initSearchByQuery(x)"
            />
          </div>
        </template>
        <template v-else>
          <FilterWrapper>
            <template #left>
              <div v-for="(x, i) in [...menuItems.catalog, ...menuItems.products]" :key="i" class="p-1">
                <FilterButton
                  icon="outlined/cats"
                  :name="x.name"
                  @click.prevent="navigateTo(x.path)"
                />
              </div>
            </template>
          </FilterWrapper>
        </template>
      </div>

      <div class="flex items-start">
        <FilterSidebar class="mr-8" />

        <div class="min-h-screen w-full max-w-full overflow-hidden">
          <div class="!min-h-[55px] mb-5 hidden w-full md:block">
            <div v-if="isPageLoading" class="flex items-center justify-between">
              <SkeletonSelect class="mr-2" />

              <div class="flex items-center">
                <SkeletonState v-for="n in 2" :key="n" class="mr-3 last:mr-0" />
              </div>
            </div>
            <FilterWrapper class="relative z-50" v-else-if="!isStateLoading">
              <template #left>
                <BasicSelect
                  class="mr-2 [&_button]:!w-[242px]"
                  :model-value="sortOptions[sequenceNumber]"
                  :options="sortOptions"
                  placeholder="Популярные"
                  @selected="handlerSort"
                />
              </template>
              <template #right>
                <!--<FilterState v-if="" title="Со скидкой" />-->
                <FilterState
                  v-if="searchboosterStore.filter.params.isRecommended"
                  class="mr-2"
                  title="Рекомендуем"
                  @click.prevent="searchboosterStore.filter.params.isRecommended = false"
                />

                <FilterState
                  v-if="searchboosterStore.filter.params.isAvailable"
                  class="mr-2"
                  title="В наличии"
                  @click.prevent="searchboosterStore.filter.params.isAvailable = false"
                />

                <template
                  v-for="[categoryId, categoryName] in Object.entries(searchboosterStore.temp.categoriesMap)"
                  :key="categoryId"
                >
                  <FilterState
                    class="mr-2"
                    :title="categoryName"
                    @click.prevent="searchboosterStore.deleteFilterByCategoryId(categoryId)"
                  />
                </template>

                <template
                  v-for="[vendorId, vendorName] in Object.entries(searchboosterStore.temp.vendorsMap)"
                  :key="vendorId"
                >
                  <FilterState
                    class="mr-2 last:mr-0"
                    :title="vendorName"
                    @click.prevent="searchboosterStore.deleteFilterByVendorId(vendorId)"
                  />
                </template>
              </template>
            </FilterWrapper>
          </div>

          <div class="w-full">
            <template v-if="getSearchOffers.length || isSearchLoading || isPageLoading">
              <div class="-mx-2 flex flex-wrap justify-start">
                <template v-if="isSearchLoading || isPageLoading">
                  <div v-for="n in 12" :key="n" class="w-1/2 p-2 lg:w-1/3 xl:w-1/4">
                    <SkeletonCard />
                  </div>
                </template>
                <template v-else>
                  <div
                    v-for="(x, index) in getSearchProducts"
                    :key="index"
                    class="w-1/2 p-2 lg:w-1/3 xl:w-1/4"
                    itemscope
                    itemtype="https://schema.org/Product"
                  >
                    <ProductCard itemprop="itemListElement" :product-data="x" />
                  </div>
                </template>
              </div>

              <Pagination
                class="mt-6"
                :current-page="currentPage"
                itemprop="pagination"
                :last-page="lastPage"
                @change-page="changePage"
              />
            </template>
            <div v-else-if="getPopularOffers.length">
              <div class="-mx-2 flex flex-wrap justify-start">
                <div
                  v-for="(x, index) in getPopularOffers"
                  :key="index"
                  class="w-1/2 p-2 lg:w-1/3 xl:w-1/4"
                  itemscope
                  itemtype="https://schema.org/Product"
                >
                  <ProductCard
                    itemprop="itemListElement"
                    :product-data="searchboosterStore.convertOfferToProductData(x)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <FilterMobilePopup ref="mobileCommonFiltersRef" :is-back="false" title="Фильтры">
      <div class="mb-3 block grow text-sm font-medium tracking-[-0.01em] text-main-dark">
        Цена, ₽
      </div>

      <FilterMobilePrice class="mb-4" />

      <FilterRadioButtonsSection />

      <FilterMobileItem
        :class="{ '!border-b-0': searchboosterStore.filter.params.categories.length }"
        title="Категория"
        @click.prevent="
          mobileFilterByCategoriesRef.openPopup();
          mobileCommonFiltersRef.closePopup()
        "
      />

      <div v-if="searchboosterStore.temp.categoriesMap" class="flex flex-wrap items-center">
        <FilterState
          v-for="[categoryId, categoryName] in Object.entries(searchboosterStore.temp.categoriesMap)"
          :key="categoryId"
          class="mb-2 mr-2"
          :title="categoryName"
          @click.prevent="searchboosterStore.deleteFilterByCategoryId(categoryId)"
        />

        <FilterState
          v-if="searchboosterStore.filter.params.categories.length"
          is-light
          title="Очистить"
          @click="searchboosterStore.clearFilterCats"
        />
      </div>

      <FilterMobileItem
        :class="{ '!border-b-0': searchboosterStore.filter.params.vendors.length }"
        title="Бренды"
        @click.prevent="
          mobileFilterByBrandsRef.openPopup();
          mobileCommonFiltersRef.closePopup()
        "
      />

      <div v-if="searchboosterStore.temp.vendorsMap" class="flex flex-wrap items-center">
        <template
          v-for="[vendorId, vendorName] in Object.entries(searchboosterStore.temp.vendorsMap)"
          :key="vendorId"
        >
          <FilterState
            class="mb-2 mr-2"
            :title="vendorName"
            @click.prevent="searchboosterStore.deleteFilterByVendorId(vendorId)"
          />
        </template>

        <FilterState
          v-if="searchboosterStore.filter.params.vendors.length"
          is-light
          title="Очистить"
          @click="searchboosterStore.clearFilterVendors"
        />
      </div>

      <FilterMobileItem
        title="Система организма"
        @click.prevent="
          mobileFilterByBodyCatsRef.openPopup();
          mobileCommonFiltersRef.closePopup()
        "
      />

      <FilterMobileItem
        title="Активное вещество"
        @click.prevent="
          mobileFilterByActiveCatsRef.openPopup();
          mobileCommonFiltersRef.closePopup()
        "
      />
    </FilterMobilePopup>

    <FilterMobilePopup ref="mobileFilterByActiveCatsRef" is-back title="Активное вещество" @back="onBack">
      <FilterActiveCatsSection />
    </FilterMobilePopup>

    <FilterMobilePopup ref="mobileFilterByBodyCatsRef" is-back title="Система организма" @back="onBack">
      <FilterBodyCatsSection />
    </FilterMobilePopup>

    <FilterMobilePopup ref="mobileFilterByBrandsRef" is-back title="Бренды" @back="onBack">
      <FilterBrandsSection />
    </FilterMobilePopup>

    <FilterMobilePopup ref="mobileFilterByCategoriesRef" is-back title="Категории" @back="onBack">
      <FilterCategoriesSection />
    </FilterMobilePopup>

    <FilterBottomPopup ref="mobileSortRef" :is-blackout-mode="false" title="Сортировка">
      <a
        v-for="x in sortOptions"
        :key="x.value"
        class="
          flex items-center justify-between whitespace-nowrap border-b-[0.5px] border-[#E2E2E2] py-3
          text-sm leading-normal tracking-tight text-main-dark !no-underline hover:bg-main-gray-low
          sm:text-base
        "
        :class="{ 'text-main-green': x.value === searchboosterStore.sort }"
        href="#"
        @click.prevent="searchboosterStore.sort = x.value; mobileSortRef.closePopup()"
      >
        <div>
          {{ x.name }}
        </div>

        <div class="mr-3">
          <SvgIcon v-if="x.value === searchboosterStore.sort" class="!size-5" name="outlined/leaky-circle" />
          <SvgIcon v-else class="!size-5" name="outlined/circle" />
        </div>
      </a>
    </FilterBottomPopup>

    <FilterBottomPopup ref="mobilePriceRef" is-blackout-mode is-button title="Цена, ₽">
      <FilterMobilePrice />
    </FilterBottomPopup>
  </section>
</template>
